import type { DossierThread } from './threadContracts';
import type {RelationsInput,RelationsResult} from './relationsContracts';
import type { FaithfulScope, FaithfulState, FaithfulPatch } from '@/uptk/core/components/moduleEntity/faithful/contracts';
import type { GapReport } from '../catalog/gapReport';
import type { BindingCandidate } from '../designer/assistedBindings';
import type {ZoneCodeInput,ZoneCodeResult} from '../designer/zoneExtensions';
import type { ModuleSpecBlock as BindingBlock } from './moduleSpecContracts';
import type { PrototypeConversionInput, PrototypeConversionEstimate, PrototypeConversionJob } from './conversionContracts';
import type { TranslationPreference, StarterTestPlanResult } from './cleanModelContracts';
import type { ModuleComponents } from './contracts';
import type { ScreenAnalysis, ScreenAnalysisInput } from './screenAnalysisContracts';
import type { CapabilityCatalog, CoverageReport, NavigationProposal } from './contracts';
import type { ReferenceCapture, ReferenceCaptureInput } from './contracts';
import type { Simulation, SimulationSummary, SimulationCreateInput, SimulationUpdateInput, SimulationGenerateInput, SimulationRefreshInput, SimulationRefreshResult, SimulationAcknowledgeInput, SimulationShareInput, SimulationShareCreated, GeneratorIssue } from './contracts';
import type { Idea, IdeaInput, IdeaEstimate, IdeaTransfer, IdeaTransferInput, IdeaRepositoryTree } from './contracts';
/**
 * Module Studio (UPT-2079 lots L0 à L5, UPT-2096 P1-a / P1-b, UPT-2095) — interface unique du dépôt de données.
 *
 * Les écrans ne connaissent QUE cette interface : ils ignorent si les données
 * viennent des jeux de démonstration JSON (`fixtureRepository`) ou de l'API
 * `/api/v1/module-studio/…` (`restRepository`). Le fournisseur
 * (`ModuleStudioDataProvider`) compose les deux, ressource par ressource,
 * selon `GET /api/v1/module-studio/capabilities`.
 *
 * Formes : `contracts.ts` (copie exacte du contrat canonique Geode-specs).
 */
import type {
  AiRoute,
  DeliveryCheck,
  DesignDossier,
  DesignDossierInput,
  DesignModuleSpecInput,
  DesignModuleSpecPayload,
  DesignSpecDetail,
  DesignSpecDiff,
  DesignSpecDocumentInput,
  DesignSpecGenerateInput,
  DesignSpecSyncInput,
  DesignSpecSyncResult,
  DesignSpecVersion,
  DesignSpecVersionSummary,
  DesignerCatalog,
  EnvironmentCode,
  Feedback,
  FeedbackClassifyInput,
  FeedbackFilter,
  FeedbackInput,
  FeedbackPage,
  FeedbackRejectInput,
  FeedbackReopenInput,
  FeedbackResolveInput,
  FeedbackSummary,
  FeedbackTransmission,
  FeedbackTransmissionInput,
  FeedbackTransmitInput,
  FeedbackUpdateInput,
  GeneratorBundleInput,
  GeneratorRepoDeposit,
  GeneratorRun,
  GeneratorRunSummary,
  GeneratorStripeInput,
  GeneratorStripeResult,
  ModuleAsset,
  ModuleAssetFromPieceInput,
  ModuleAssetInput,
  ModuleSpecDraft,
  ModuleSpecDraftInput,
  ModuleSpecDraftProposeInput,
  ModuleSpecExportPreview,
  ModuleSpecExportResult,
  PipelineApproveInput,
  PipelineDataRunInput,
  PipelineEnvironment,
  PipelineEnvironmentInput,
  PipelineEnvironmentsPayload,
  PipelineExecuteInput,
  PipelineGateTests,
  PipelineJourney,
  PipelineKind,
  PipelineLicenseResult,
  PipelineRun,
  PipelineRunSummary,
  PipelineTenantOption,
  PrototypeAcknowledgeInput,
  PrototypeAnalysis,
  PrototypeAnalysisApplyInput,
  PrototypeAnalysisApplyResult,
  PrototypeAnalysisEstimate,
  PrototypeAnalysisInput,
  PrototypeAnalysisRejectInput,
  PrototypeAnalysisSummary,
  PrototypeImageInput,
  PrototypeImport,
  PrototypeImportInput,
  PrototypeImportPatch,
  PrototypeImportSummary,
  PrototypeInventory,
  PrototypePresentationResponse,
  PrototypePrompt,
  PreviewAcknowledgeInput,
  PreviewDataset,
  PreviewDatasetCreateInput,
  PreviewDatasetSummary,
  PreviewDatasetUpdateInput,
  PreviewGenerateInput,
  PreviewRefreshInput,
  PreviewRefreshResult,
  PreviewShareCreated,
  PreviewShareInput,
  PrototypeSourceInput,
  PrototypeSnapshotInput,
  PrototypeSnapshotResult,
  SpecDocument,
  TestPlan,
  TestPlanSummary,
  TestResult,
  TestResultInput,
  TestRun,
  TestRunSummary,
} from './contracts';
import type { ModuleSpecTranslationEstimate, ModuleSpecTranslationJob } from './contracts';
import type {
  ModuleSpecDecisionInput,
  ModuleSpecDiffSection,
  ModuleSpecDocument,
  ModuleSpecProposeInput,
  ModuleSpecRepairPreview,
  ModuleSpecVersion,
  ModuleSpecVersionSummary,
  ValidationReport,
} from './moduleSpecContracts';
import type { PreparedHtml, PreparedImage, PrototypeUploadFiles } from '../prototypes/files';

/** Ressources à bascule indépendante (capabilités du backend). */
export type ModuleStudioResource =
  | 'faithfulState'
  | 'computedFields'
  | 'moduleComponents'
  | 'referenceCaptures'
  | 'ideas'
  | 'dossiers'
  | 'specs'
  | 'testPlans'
  | 'testRuns'
  | 'prototypeImports'
  | 'prototypeAnalysis'
  | 'moduleSpec'
  | 'designer'
  | 'preview'
  | 'generator'
  | 'generatorStripe'
  | 'generatorRepo'
  | 'pipeline'
  | 'pipelineData'
  | 'pipelineGates'
  | 'feedback'
  | 'deliveryCheck'
  | 'presentation'
  | 'customPages'
  | 'translation'
  | 'simulation'
  | 'capabilityCatalog'
  | 'navigationFidelity'
  | 'translationJobs'
  | 'conversionJobs';

/** Témoin admis d'un composant fourni par l'analyse IA (`residuals[path]`) ; `revision` 0 = source de l'analyse, jamais modifiée. */
export interface ScreenCodeSource{path:string;source:string;revision?:number;revisions?:number;editedAt?:string;editedBy?:string;originals?:import('./moduleSpecContracts').ModuleSpecBlock[]}
export interface ScreenCodeSourceCheck{path:string;accepted:boolean;violations:{rule:string;line:number;excerpt?:string;expected?:string}[];diagnostics:{code:string;line:number;message:string}[]}
export interface ModuleStudioRepository {
  getFaithfulState(scope: FaithfulScope): Promise<FaithfulState>;
  putFaithfulState(scope: FaithfulScope, input: FaithfulPatch & { baseRevision: number }): Promise<{revision:number}>;
  estimatePrototypeConversion?(importId: string, input: PrototypeConversionInput): Promise<PrototypeConversionEstimate>;
  startPrototypeConversion?(importId: string, input: PrototypeConversionInput): Promise<PrototypeConversionJob>;
  listPrototypeConversions?(importId: string): Promise<PrototypeConversionJob[]>;
  getPrototypeConversion?(importId: string, id: string): Promise<PrototypeConversionJob>;
  cancelPrototypeConversion?(importId: string, id: string): Promise<PrototypeConversionJob>;
  resumePrototypeConversion?(importId: string, id: string): Promise<PrototypeConversionJob>;
  getTranslationPreference(dossierId: string): Promise<TranslationPreference>;
  setTranslationPreference(dossierId: string, preference: TranslationPreference): Promise<TranslationPreference>;
  createStarterTestPlan(dossierId: string, version: number): Promise<StarterTestPlanResult>;
  estimateScreenAnalysis?(importId: string, input: import('./screenAnalysisContracts').ScreenAnalysisInput): Promise<import('./screenAnalysisContracts').ScreenAnalysisEstimate>;
  startScreenAnalysis?(importId: string, input: ScreenAnalysisInput): Promise<ScreenAnalysis>;
  listScreenAnalyses?(importId: string): Promise<ScreenAnalysis[]>;
  getScreenAnalysis?(importId: string, id: string): Promise<ScreenAnalysis>;
  applyScreenAnalysis?(importId: string, id: string): Promise<ScreenAnalysis>;
  rejectScreenAnalysis?(importId: string, id: string): Promise<ScreenAnalysis>;
  produceScreenCode?(importId:string,id:string,input:{blockKey:string;includeMaskedMarkup:boolean}):Promise<ScreenAnalysis>;
  zoneCode?(importId:string,input:ZoneCodeInput,estimate:boolean):Promise<ZoneCodeResult>;
  getScreenCodeSource?(dossierId:string,artifactRef:string,path:string):Promise<ScreenCodeSource>;
  /** GAP-76 (02.11.2026) : la même porte qu'à la livraison, sans rien écrire. */
  checkScreenCodeSource?(dossierId:string,artifactRef:string,path:string,source:string):Promise<ScreenCodeSourceCheck>;
  /** GAP-76 : remplace le témoin admis ; refus 409 `code.gate_refused` avec `details.violations` / `details.diagnostics`. */
  saveScreenCodeSource?(dossierId:string,artifactRef:string,path:string,source:string):Promise<ScreenCodeSource>;
  listSimulations(dossierId: string): Promise<SimulationSummary[]>;
  getSimulation(dossierId: string, simulationId: string): Promise<Simulation | null>;
  createSimulation(dossierId: string, input: SimulationCreateInput): Promise<Simulation>;
  updateSimulation(dossierId: string, simulationId: string, input: SimulationUpdateInput): Promise<Simulation>;
  generateSimulationData(dossierId: string, simulationId: string, input: SimulationGenerateInput): Promise<Simulation>;
  refreshSimulation(dossierId: string, simulationId: string, input: SimulationRefreshInput): Promise<SimulationRefreshResult>;
  acknowledgeSimulationWarnings(dossierId: string, simulationId: string, input: SimulationAcknowledgeInput): Promise<Simulation>;
  deleteSimulation(dossierId: string, simulationId: string): Promise<void>;
  createSimulationShare(dossierId: string, simulationId: string, input: SimulationShareInput): Promise<SimulationShareCreated>;
  revokeSimulationShare(dossierId: string, simulationId: string): Promise<void>;

  listIdeas(): Promise<Idea[]>;
  createIdea(input: IdeaInput): Promise<Idea>;
  getIdea(id: string): Promise<Idea>;
  updateIdea(id: string, input: IdeaInput): Promise<Idea>;
  ideaEstimate(id: string, phase: 'brief' | 'prototype'): Promise<IdeaEstimate>;
  fetchIdeaRepository(id: string): Promise<Idea>;
  getIdeaRepository(id: string): Promise<IdeaRepositoryTree>;
  startBrief(id: string): Promise<Idea>;
  acceptBrief(id: string, input: { text: string }): Promise<Idea>;
  startPrototype(id: string, input?: { remarksText?: string }): Promise<Idea>;
  getPrototypeHtml(id: string): Promise<string>;
  getIdeaPrompt(id: string): Promise<string>;
  cancelIdea(id: string): Promise<Idea>;
  transferIdea(id: string, input: IdeaTransferInput): Promise<IdeaTransfer>;
  deleteIdea(id: string): Promise<void>;

  listDossiers(): Promise<DesignDossier[]>;
  /** 404 dossier.not_found → null. */
  getDossierThread(id: string, displayedAnalysis?: string): Promise<DossierThread>;
  getDossier(id: string): Promise<DesignDossier | null>;
  /** `input.id` présent → PUT (mise à jour), absent → POST (création). */
  saveDossier(input: DesignDossierInput): Promise<DesignDossier>;

  /* ── Dossiers servis (UPT-2079 L2 → 'dossiers') ───────────────────────── */
  /** 409 dossier.in_use (message `test-plans` | `prototype-imports`) quand le dossier est utilisé. */
  deleteDossier(id: string): Promise<void>;
  getModuleSpec(dossierId: string): Promise<DesignModuleSpecPayload>;
  putModuleSpec(dossierId: string, input: DesignModuleSpecInput): Promise<DesignDossier>;

  /* ── Specs (UPT-2079 L2 → 'specs') ─────────────────────────────────────── */
  listSpecs(dossierId: string): Promise<SpecDocument[]>;
  /** 404 spec.not_found → null. */
  getSpec(id: string): Promise<DesignSpecDetail | null>;
  createSpec(dossierId: string, input: DesignSpecDocumentInput): Promise<DesignSpecDetail>;
  updateSpec(id: string, input: DesignSpecDocumentInput): Promise<DesignSpecDetail>;
  listSpecVersions(id: string): Promise<DesignSpecVersionSummary[]>;
  getSpecVersion(id: string, version: number): Promise<DesignSpecVersion>;
  /** Bornes nulles = valeurs par défaut du serveur (`to` = version courante, `from` = `to` − 1). */
  diffSpec(id: string, from: number | null, to: number | null): Promise<DesignSpecDiff>;
  /** Squelette du gabarit sans IA : version `agent`, toujours créée. */
  generateSpec(id: string, input: DesignSpecGenerateInput): Promise<DesignSpecDetail>;
  syncSpec(id: string, input: DesignSpecSyncInput): Promise<DesignSpecSyncResult>;

  listTestPlans(filter?: { dossierId?: string; moduleCode?: string }): Promise<TestPlanSummary[]>;
  getTestPlan(id: string): Promise<TestPlan | null>;
  saveTestPlan(plan: TestPlan): Promise<TestPlan>;
  listTestRuns(planId: string): Promise<TestRunSummary[]>;
  getTestRun(id: string): Promise<TestRun | null>;
  createTestRun(input: {
    planId: string;
    environmentCode: EnvironmentCode;
    testerName: string;
  }): Promise<TestRun>;
  putTestResult(runId: string, stepId: string, input: TestResultInput): Promise<TestResult>;
  /** Décocher : l'étape redevient « non cochée » (aucun résultat). */
  clearTestResult(runId: string, stepId: string): Promise<void>;
  validateProposedResult(
    runId: string,
    stepId: string,
    decision: 'accept' | 'reject'
  ): Promise<TestResult>;
  putSceneNote(runId: string, sceneId: string, noteMd: string): Promise<void>;
  closeTestRun(runId: string, input: { reportMd: string; paperclipRef: string | null }): Promise<TestRun>;

  /* ── Import de prototypes (UPT-2096 P1-a → 'prototypeImports') ─────── */
  listPrototypeImports(dossierRef: string): Promise<PrototypeImportSummary[]>;
  getPrototypeImport(id: string): Promise<PrototypeImport>;
  /** L2 : `files.markdowns` (parties `markdown`), `input.htmlCount` / `documents` ; seul `files.html` (rang 0) est envoyé. */
  createPrototypeImport(
    dossierRef: string,
    input: PrototypeImportInput,
    files: PrototypeUploadFiles
  ): Promise<PrototypeImport>;
  /** L2 : source HTML de rang ≥ 1, une par requête ; 409 prototype.sources_complete quand `htmlExpected` est atteint. */
  addPrototypeSource(
    id: string,
    html: PreparedHtml,
    input: PrototypeSourceInput | null
  ): Promise<PrototypeImport>;
  addPrototypeImage(
    id: string,
    image: PreparedImage,
    input: PrototypeImageInput | null
  ): Promise<PrototypeImport>;
  /** 404 prototype.prompt_absent → null. */
  getPrototypePrompt(id: string): Promise<PrototypePrompt | null>;
  /** Pièces html, snapshot et markdown, lues en texte UTF-8 (jamais re-typées). */
  getPrototypePieceText(id: string, pieceId: string): Promise<string>;
  /** Pièce image, re-typée image/png après vérification de la signature. */
  getPrototypePieceImage(id: string, pieceId: string): Promise<Blob>;
  deletePrototypeSnapshot(id: string, pieceId: string): Promise<PrototypeImport>;
  addPrototypeSnapshot(id: string, input: PrototypeSnapshotInput): Promise<PrototypeSnapshotResult>;
  putPrototypeInventory(id: string, inventory: PrototypeInventory): Promise<PrototypeImport>;
  getPrototypeInventory(id: string): Promise<PrototypeInventory>;
  getModuleComponents(dossierId: string): Promise<ModuleComponents>;
  refreshModuleComponents(dossierId: string): Promise<ModuleComponents>;
  proposeBindings(spec: ModuleSpecDocument, blocks: BindingBlock[]): Promise<BindingCandidate[][]>;
  getCapabilityCatalog(dossierId?: string): Promise<CapabilityCatalog>;
  getPrototypeCoverage(id: string): Promise<CoverageReport>;
  getGapReport(dossierId: string, version: number): Promise<GapReport>;
  getSpecCoverage(dossierId: string, version: number): Promise<CoverageReport>;
  /** F2 → 'navigationFidelity' : la navigation déduite du menu du prototype pour la version (409 `navigation.no_menu` sans menu lisible). */
  /** `draft` (recette Change Management, 25.10.2026) : proposer sur le brouillon de l'auteur (pages compilées), à défaut la version. */
  getNavigationProposal(dossierId: string, version: number, importId?: string, options?: { draft?: boolean }): Promise<NavigationProposal>;
  patchPrototypeImport(id: string, patch: PrototypeImportPatch): Promise<PrototypeImport>;
  deletePrototypeImport(id: string): Promise<void>;
  prototypeRemovalImpact(id: string): Promise<{ id: string; title: string; pieces: number; analyses: number; versions: number[]; protectedSource: boolean; simulations: number; captures: number; purgeAfter: string | null; history: { action: string; at: string; actor: string }[] }>;
  restorePrototypeImport(id: string): Promise<void>;
  hidePrototypeImport(id: string): Promise<void>;

  /* ── Analyse des prototypes (UPT-2096 P1-b) ─────────────────────────── */
  /** → 'prototypeImports' (même ressource que l'import) ; 409 prototype.warnings_changed si l'empreinte diffère. */
  acknowledgePrototypeWarnings(id: string, input: PrototypeAcknowledgeInput): Promise<PrototypeImport>;
  /** L2 → 'prototypeAnalysis' : l'entrée que recevrait le modèle maintenant (taille, blocs, coût au plus). */
  getPrototypeAnalysisEstimate(id: string): Promise<PrototypeAnalysisEstimate>;
  /** → 'prototypeAnalysis' : voie que prendrait une analyse lancée maintenant par l'utilisateur courant. */
  getAiRoute(): Promise<AiRoute>;
  /**
   * Analyse ASYNCHRONE : 202 avec l'analyse au statut `running` (l'exécution, ≤ PROTOTYPE_ANALYSIS_LIMITS.timeoutSeconds,
   * se poursuit sur le serveur) ; suivre par `getPrototypeAnalysis` jusqu'à `done` ou `failed`. Un ancien serveur répond
   * 201 avec le statut déjà final : même contrat de retour.
   */
  startPrototypeAnalysis(id: string, input: PrototypeAnalysisInput): Promise<PrototypeAnalysis>;
  listPrototypeAnalyses(id: string): Promise<PrototypeAnalysisSummary[]>;
  getPrototypeAnalysis(id: string, analysisId: string): Promise<PrototypeAnalysis>;
  applyPrototypeAnalysis(
    id: string,
    analysisId: string,
    input: PrototypeAnalysisApplyInput
  ): Promise<PrototypeAnalysisApplyResult>;
  rejectPrototypeAnalysis(
    id: string,
    analysisId: string,
    input: PrototypeAnalysisRejectInput
  ): Promise<PrototypeAnalysis>;

  /* ── Versions de Module Spec (UPT-2095 §11.2 → 'moduleSpec', `/api/v1/module-spec/…`) ── */
  /** Versions du dossier (sans le JSON), ordre du serveur (version décroissante). */
  listModuleSpecVersions(dossierId: string): Promise<ModuleSpecVersionSummary[]>;
  /** 404 modulespec.not_found si la version n'existe pas. */
  getModuleSpecVersion(dossierId: string, version: number): Promise<ModuleSpecVersion>;
  /** Différences par section (`rights` comprise) ; sections vides omises. */
  diffModuleSpecVersions(dossierId: string, from: number, to: number): Promise<ModuleSpecDiffSection[]>;
  /** Dépose une version `proposed` (`source` absent = `human`) ; jamais bloquant sur le rapport. */
  proposeModuleSpecVersion(dossierId: string, input: ModuleSpecProposeInput): Promise<ModuleSpecVersion>;
  /** 409 modulespec.not_proposed · 400 modulespec.has_errors (accept) · 400 modulespec.comment_required (reject). */
  decideModuleSpecVersion(dossierId: string, version: number, input: ModuleSpecDecisionInput): Promise<ModuleSpecVersion>;
  /** UPT-2079 L11 §9 : aperçu de la réparation d'une version `proposed` (`GET …/versions/{n}/repair`), rien n'est écrit ; 404 modulespec.not_found. */
  previewRelations(input:RelationsInput):Promise<RelationsResult>;
  applyRelations(input:RelationsInput):Promise<RelationsResult>;
  previewModuleSpecRepair(dossierId: string, version: number): Promise<ModuleSpecRepairPreview>;
  /** `POST …/versions/{n}/repair` : crée une NOUVELLE version `proposed` (la version `n` reste inchangée) · 409 modulespec.repair.nothing · 409 modulespec.not_proposed. */
  repairModuleSpecVersion(dossierId: string, version: number): Promise<ModuleSpecVersion>;
  /**
   * UPT-2079 L12b (→ 'deliveryCheck') : le contrôle de livraison à blanc d'une version `proposed`, `accepted` ou `superseded` (`GET …/versions/{n}/delivery-check`), aucune écriture ;
   * 404 modulespec.not_found (brouillon ou version inconnue). Pas de cache : chaque lecture est un nouveau contrôle.
   */
  getDeliveryCheck(dossierId: string, version: number): Promise<DeliveryCheck>;
  estimateModuleSpecTranslation(dossierId: string, version: number): Promise<ModuleSpecTranslationEstimate>;
  translateModuleSpecVersion(dossierId: string, version: number): Promise<ModuleSpecVersion>;
  /**
   * UPT-2079 P2b (→ 'translationJobs') : traduction en tâche de fond. `create…` : `POST …/versions/{n}/translation-jobs` sans corps, 202 ; `list…` : tâches du dossier, de la plus
   * récente à la plus ancienne (`limit` 1 à 20) ; `get…` : une tâche (le serveur rend `interrupted` une tâche au bail expiré) ; `cancel…` : 200 (une tâche `running` reste `running` avec
   * `cancelRequested`, le web sonde) ; `resume…` : 202. Erreurs `{code, message}` : `modulespec.translate.{running, busy, job_not_found, not_resumable, not_cancellable}`.
   */
  createModuleSpecTranslationJob(dossierId: string, version: number): Promise<ModuleSpecTranslationJob>;
  listModuleSpecTranslationJobs(dossierId: string, limit?: number): Promise<ModuleSpecTranslationJob[]>;
  getModuleSpecTranslationJob(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob>;
  cancelModuleSpecTranslationJob(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob>;
  resumeModuleSpecTranslationJob(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob>;
  /**
   * UPT-2079 L14a (→ 'presentation') : ressources image du dossier (`/dossiers/{code}/assets…`). `listModuleAssets` : triées par date puis empreinte, sans contenu ;
   * `uploadModuleAsset` : multipart `image` + `metadata`, `rightsAttested: true` obligatoire (400 asset.rights_required), même empreinte déjà présente → l'existante ;
   * `copyModuleAssetFromPiece` : copie d'une pièce `image` d'un import du dossier ; `getModuleAssetBytes` : octets stockés (adressés par empreinte) ;
   * `deleteModuleAsset` : 409 asset.in_use si une version `proposed` / `accepted` la cite. Codes `asset.*` : `ASSET_ERROR_STATUS`.
   */
  listReferenceCaptures(dossierId: string): Promise<ReferenceCapture[]>;
  uploadReferenceCapture(dossierId: string, image: Blob, metadata: ReferenceCaptureInput): Promise<ReferenceCapture>;
  getReferenceCaptureBytes(dossierId: string, sha256: string, thumb?: boolean): Promise<ArrayBuffer>;
  deleteReferenceCapture(dossierId: string, sha256: string): Promise<void>;
  listModuleAssets(dossierId: string): Promise<ModuleAsset[]>;
  uploadModuleAsset(dossierId: string, image: Blob, metadata: ModuleAssetInput): Promise<ModuleAsset>;
  copyModuleAssetFromPiece(dossierId: string, input: ModuleAssetFromPieceInput): Promise<ModuleAsset>;
  getModuleAssetBytes(dossierId: string, sha256: string): Promise<ArrayBuffer>;
  deleteModuleAsset(dossierId: string, sha256: string): Promise<void>;
  /** UPT-2079 L14a (→ 'presentation') : l'habillage calculé depuis le style mesuré d'un import (`GET …/prototype-imports/{id}/presentation`), aucune écriture. */
  getPrototypePresentation(importId: string): Promise<PrototypePresentationResponse>;
  /** Rapport du validateur sur un document local ; aucune écriture. */
  validateModuleSpec(spec: ModuleSpecDocument): Promise<ValidationReport>;
  /** Même route sur un TEXTE (`application/json` ou `application/yaml`) envoyé tel quel ; aucune écriture. */
  validateModuleSpecText(text: string, format: 'json' | 'yaml'): Promise<ValidationReport>;

  /* ── Concepteur (UPT-2079 L3 → 'designer', `/api/v1/module-spec/dossiers/{code}/draft…`, `…/export…`) ── */
  /** Brouillon de l'auteur courant ; 404 modulespec.draft.not_found → null. */
  getModuleSpecDraft(dossierId: string): Promise<ModuleSpecDraft | null>;
  /** Crée ou met à jour le brouillon (`baseChecksum`) ; 409 modulespec.draft.conflict (message = checksum courant). */
  putModuleSpecDraft(dossierId: string, input: ModuleSpecDraftInput): Promise<ModuleSpecDraft>;
  /** Copie d'une version du dossier ; 404 modulespec.not_found · 409 modulespec.draft.exists. */
  createDraftFromVersion(dossierId: string, version: number, archiveChecksum?: string): Promise<ModuleSpecDraft>;
  /** Dépose le brouillon en version `proposed` (`human`) et le supprime. Avec `feedbackIds` : 409 `feedback.state` · `feedback.category` · `feedback.version_not_newer` (message = numéro du retour), sans rien écrire. */
  proposeModuleSpecDraft(dossierId: string, input: ModuleSpecDraftProposeInput): Promise<ModuleSpecVersion>;
  deleteModuleSpecDraft(dossierId: string): Promise<void>;
  /** Le module installé devient le brouillon de l'auteur ; 404 modulespec.export.module_not_found · 409 modulespec.draft.exists. */
  exportModuleToDraft(dossierId: string, moduleCode: string): Promise<ModuleSpecExportResult>;
  /** Aperçu de l'export sans écriture. */
  previewModuleExport(moduleCode: string): Promise<ModuleSpecExportPreview>;
  /** → 'moduleSpec' : catalogue de la plateforme (types, validateurs, transformeurs, modules installés). */
  getDesignerCatalog(): Promise<DesignerCatalog>;

  /* ── Preview (UPT-2079 L4 → 'preview', `/api/v1/module-studio/dossiers/{code}/preview-datasets…`) ── */
  /** Jeux du dossier, du plus récent au plus ancien, sans lignes ni instantané. */
  listPreviewDatasets(dossierId: string): Promise<PreviewDatasetSummary[]>;
  /** 404 preview.dataset.not_found → null. */
  getPreviewDataset(dossierId: string, datasetId: string): Promise<PreviewDataset | null>;
  /** Source : brouillon de l'auteur ou version numérotée ; `generate` et `rows` ensemble → 400 preview.dataset.invalid (`rows`). */
  createPreviewDataset(dossierId: string, input: PreviewDatasetCreateInput): Promise<PreviewDataset>;
  /** « Enregistrer le jeu » : l'unique PUT ; `baseRevision` périmée → 409 preview.dataset.conflict (message = révision courante). */
  updatePreviewDataset(dossierId: string, datasetId: string, input: PreviewDatasetUpdateInput): Promise<PreviewDataset>;
  /** Remplace les lignes par `count` lignes d'exemple déterministes (graine absente = celle du jeu). */
  generatePreviewRows(dossierId: string, datasetId: string, input: PreviewGenerateInput): Promise<PreviewDataset>;
  /** Relit la source, reprojette l'entité du jeu ; `droppedKeys` = colonnes retirées des lignes. */
  refreshPreviewSnapshot(dossierId: string, datasetId: string, input: PreviewRefreshInput): Promise<PreviewRefreshResult>;
  /** 409 preview.dataset.warnings_changed quand l'empreinte diffère de la courante. */
  acknowledgePreviewWarnings(dossierId: string, datasetId: string, input: PreviewAcknowledgeInput): Promise<PreviewDataset>;
  deletePreviewDataset(dossierId: string, datasetId: string): Promise<void>;
  /** 201 : le seul moment où le jeton existe en clair ; 409 preview.share.unacknowledged. */
  createPreviewShare(dossierId: string, datasetId: string, input: PreviewShareInput): Promise<PreviewShareCreated>;
  /** Idempotent : aucun lien actif → 204 aussi. */
  revokePreviewShare(dossierId: string, datasetId: string): Promise<void>;

  /* ── Générateur (UPT-2079 L5 → 'generator', `/api/v1/module-studio/dossiers/{code}/generation-runs…`) ── */
  /** Exécutions du dossier (bundle et stripe), du plus récent au plus ancien, sans plan. */
  listGenerationRuns(dossierId: string): Promise<GeneratorRunSummary[]>;
  /** 404 generator.run.not_found → null. */
  getGenerationRun(dossierId: string, runId: string): Promise<GeneratorRun | null>;
  /** Première date libre après les migrations du classpath serveur. */
  /** GAP-60 : avec le dossier, la date proposée dépasse sa dernière génération réussie. */
  suggestedMigrationDate?(dossierId?: string): Promise<string>;
  /**
   * Produit le plan et le bundle d'une version ACCEPTÉE ; le serveur répond 201 même quand le plan a des erreurs (le statut
   * `failed` se lit dans le corps) ; 409 generator.spec_not_accepted · 400 generator.invalid · 400 generator.run.limit.
   */
  createGenerationRun(dossierId: string, input: GeneratorBundleInput): Promise<GeneratorRun>;
  deleteGenerationRun(dossierId: string, runId: string): Promise<void>;
  /** Contenu texte (UTF-8) d'UN fichier du plan ; 404 generator.file.not_found · 409 generator.bundle.not_available. */
  readGeneratorFile(dossierId: string, runId: string, path: string): Promise<string>;
  /** Archive ZIP de l'exécution ; 409 generator.bundle.not_available. */
  downloadGeneratorBundle(dossierId: string, runId: string, installation?: boolean): Promise<Blob>;

  /* ── Stripe en mode test (UPT-2079 L5c → 'generatorStripe', `…/dossiers/{code}/stripe`) ── */
  /** `apply: false` simule (rien n'est écrit) ; 403 generator.stripe.platform_only · 409 key_missing / key_not_test / catalog_conflict · 502 failed. */
  runGeneratorStripe(dossierId: string, input: GeneratorStripeInput): Promise<GeneratorStripeResult>;

  /* ── Dépôt dans git (UPT-2079 L5d → 'generatorRepo', `…/generation-runs/{id}/repo`) ── */
  /**
   * Dépose les fichiers de l'archive dans le dépôt git du module, sur une branche dédiée (rien n'est fusionné). 200
   * `GeneratorRepoDeposit` (`pushed` ou `unchanged`) ; 404 generator.run.not_found · 409 generator.bundle.not_available ·
   * 409 generator.repo.unavailable / refused / branch_exists (le `message` de unavailable et refused est un jeton de raison) ·
   * 502 generator.repo.failed (message = raison).
   */
  depositGeneratorRun(dossierId: string, runId: string, options?: { createRepository?: boolean; openPullRequest?: boolean; suffix?: string }): Promise<GeneratorRepoDeposit>;

  /* ── Pipeline CODE > DATA (UPT-2079 L6a → 'pipeline', `/api/v1/module-studio/pipeline/…`) ──────────────────
   * Sept méthodes de la ressource `pipeline` : registre des environnements, relevé CODE en lecture, historique.
   * `pipelineData` (copie de données, L6b, trois méthodes plus bas) et `pipelineGates` (portes, approbation, parcours, L6c) ajoutent leurs
   * méthodes dans leur incrément. Aucune de ces méthodes ne déclenche quoi que ce soit dans Azure DevOps. */
  /** Registre (quatre lignes) et relevé de chacun ; le premier appel insère les lignes par défaut côté serveur. */
  getPipelineEnvironments(): Promise<PipelineEnvironmentsPayload>;
  /** Remplace les champs modifiables d'une ligne ; 400 pipeline.invalid (message = champ) · 404 pipeline.env.not_found · 409 pipeline.run.state (`running`). */
  updatePipelineEnvironment(code: EnvironmentCode, input: PipelineEnvironmentInput): Promise<PipelineEnvironment>;
  /** Locataires de l'hôte (`GET /api/tenants/options`, hors `/api/v1/module-studio`) ; chargés à l'ouverture du tiroir du registre. */
  listPipelineTenants(): Promise<PipelineTenantOption[]>;
  /** Du plus récent au plus ancien ; filtres absents non envoyés ; `limit` 1 à 100 (défaut serveur 30). */
  listPipelineRuns(filter?: { kind?: PipelineKind; env?: EnvironmentCode; moduleCode?: string; limit?: number }): Promise<PipelineRunSummary[]>;
  /** 404 pipeline.run.not_found → null. */
  getPipelineRun(id: string): Promise<PipelineRun | null>;
  /** Relevé CODE de `env` : UN `POST` ; le serveur répond 201 `succeeded` ou `failed` (un relevé raté se lit dans le corps, jamais dans le code HTTP) ; 409 pipeline.env.unavailable · pipeline.run.state (`running`). */
  readPipelineEnvironment(env: EnvironmentCode): Promise<PipelineRun>;
  /** 409 pipeline.run.state pour une exécution `running`, ou DATA `succeeded` / `rolled-back`. */
  deletePipelineRun(id: string): Promise<void>;

  /* ── Copie de données d'un locataire à un autre (UPT-2079 L6b → 'pipelineData', `…/pipeline/runs` DATA) ─────────
   * Trois méthodes de la ressource `pipelineData` : lire le plan (rien n'est copié), exécuter, retourner en arrière. La copie se fait dans
   * la base de l'environnement courant, vers un locataire DÉCLARÉ par le registre (jamais saisi), dans des tables VIDES de la cible. */
  /**
   * Plan de copie (`POST …/pipeline/runs`, corps `{kind:'DATA', …}`, champs absents non envoyés) : 201 `planned` MÊME quand le plan porte
   * des erreurs (lues dans `data.issues`, jamais déduites du code HTTP) ; 400 pipeline.invalid (message = champ) · pipeline.run.limit ·
   * 404 pipeline.module.not_found · 409 pipeline.env.unavailable (message = jeton de raison).
   */
  planPipelinePromotion(input: Omit<PipelineDataRunInput, 'kind'>): Promise<PipelineRun>;
  /**
   * Exécute un plan relu (`POST …/runs/{id}/execute`, corps `{planSha256}` = empreinte COMPLÈTE) : 200 `succeeded` avec les comptes ;
   * 400 pipeline.invalid (`planSha256`) · 409 pipeline.run.state · pipeline.data.blocked · pipeline.data.plan_changed · pipeline.data.failed
   * (message = raison) · pipeline.gate.closed (dès L6c).
   */
  executePipelineRun(id: string, input: PipelineExecuteInput): Promise<PipelineRun>;
  /** Retour arrière (`POST …/runs/{id}/rollback`, sans corps) : 200 `rolled-back` ; 409 pipeline.rollback.unavailable (message = `not-succeeded` · `not-latest` · `data-changed`). */
  rollbackPipelineRun(id: string): Promise<PipelineRun>;

  /* ── Conditions avant la copie, approbation, parcours d'un module (UPT-2079 L6c → 'pipelineGates', `…/pipeline/…`) ─────
   * Quatre méthodes de la ressource `pipelineGates` : approuver un plan, relire la condition « tests », lire le parcours d'un module, ouvrir un
   * environnement pour un module (licence). Aucune ne copie, ne publie ni ne déclenche quoi que ce soit dans Azure DevOps. */
  /**
   * Approuve un plan (`POST …/runs/{id}/approve`, corps `{planSha256}` = empreinte COMPLÈTE) : 200 avec l'exécution (conditions réévaluées) ;
   * 400 pipeline.invalid (`planSha256`) · 403 (droit `ALL` manquant) · 409 pipeline.run.state · pipeline.data.blocked · pipeline.approval.self.
   */
  approvePipelineRun(id: string, input: PipelineApproveInput): Promise<PipelineRun>;
  /** Condition « tests réussis » d'un module sur un environnement (`GET …/modules/{code}/gates?env=`) ; 404 pipeline.module.not_found. */
  getPipelineGates(moduleCode: string, env: EnvironmentCode): Promise<PipelineGateTests>;
  /** Parcours d'un module généré (`GET …/modules/{code}/journey`), calculé sans écriture. */
  getPipelineJourney(moduleCode: string): Promise<PipelineJourney>;
  /**
   * Ouvre le module pour le locataire d'un environnement (`POST …/modules/{code}/environments/{env}/license`, sans corps) : la licence que poserait
   * un administrateur, sans rien publier ; 404 pipeline.module.not_found · 409 pipeline.env.unavailable · pipeline.license.failed (message = raison).
   */
  openPipelineEnvironment(moduleCode: string, env: EnvironmentCode): Promise<PipelineLicenseResult>;

  /* ── Retours de recette classés (UPT-2079 L10a → 'feedback', `/api/v1/module-studio/feedback…`) ─────────────────
   * Dix méthodes de la ressource `feedback`. Le serveur CRÉE les retours (un résultat d'étape `fail` ou `blocked`) ; le web ne « signale »
   * jamais une étape : `createFeedback` sert à la saisie à la main. Tout geste est groupé (1 à `FEEDBACK_LIMITS.batchMax` identifiants, tout
   * ou rien). Les erreurs (400 `feedback.invalid` dont le message est le champ, 404 `feedback.not_found`, 409 `feedback.state` ·
   * `feedback.category` · `feedback.spec.unavailable`, 400 `feedback.spec_path.unknown`, 400 `feedback.limit`) passent par `readDesignError`. */
  /** Du plus récent au plus ancien ; filtres absents non envoyés, tableaux joints par des virgules. */
  listFeedback(filter?: FeedbackFilter): Promise<FeedbackPage>;
  /** 404 feedback.not_found → null. */
  getFeedback(id: string): Promise<Feedback | null>;
  /** Saisie à la main (201). */
  createFeedback(input: FeedbackInput): Promise<Feedback>;
  /** Champs descriptifs d'un retour `open`, `classified` ou `routed` ; 409 feedback.state sinon. */
  updateFeedback(id: string, input: FeedbackUpdateInput): Promise<Feedback>;
  classifyFeedback(input: FeedbackClassifyInput): Promise<FeedbackSummary[]>;
  /** `generator` classés seulement : un ticket `UPT-n` saisi par une personne les achemine. */
  transmitFeedback(input: FeedbackTransmitInput): Promise<FeedbackSummary[]>;
  /** Texte à copier (lecture, aucune écriture) : un `POST` en lecture. */
  getFeedbackTransmission(input: FeedbackTransmissionInput): Promise<FeedbackTransmission>;
  resolveFeedback(input: FeedbackResolveInput): Promise<FeedbackSummary[]>;
  rejectFeedback(input: FeedbackRejectInput): Promise<FeedbackSummary[]>;
  reopenFeedback(input: FeedbackReopenInput): Promise<FeedbackSummary[]>;
}

/** Levée quand on appelle une méthode REST dont la ressource n'est pas servie. */
export class NotServedError extends Error {
  readonly resource: ModuleStudioResource;

  constructor(resource: ModuleStudioResource, method: string) {
    super(`module-studio: la ressource « ${resource} » n'est pas servie par l'API (${method})`);
    this.name = 'NotServedError';
    this.resource = resource;
  }
}

/** Ressource à laquelle appartient chaque méthode (bascule fixture / REST). */
export const METHOD_RESOURCE: Record<keyof ModuleStudioRepository, ModuleStudioResource> = {
  getFaithfulState: 'faithfulState',
  putFaithfulState: 'faithfulState',
  proposeBindings: 'designer',
  estimatePrototypeConversion: 'conversionJobs', startPrototypeConversion: 'conversionJobs', listPrototypeConversions: 'conversionJobs',
  getPrototypeConversion: 'conversionJobs', cancelPrototypeConversion: 'conversionJobs', resumePrototypeConversion: 'conversionJobs',
  getTranslationPreference: 'translationJobs', setTranslationPreference: 'translationJobs', createStarterTestPlan: 'moduleSpec',
  listScreenAnalyses: 'prototypeAnalysis', estimateScreenAnalysis: 'prototypeAnalysis', startScreenAnalysis: 'prototypeAnalysis', getScreenAnalysis: 'prototypeAnalysis', applyScreenAnalysis: 'prototypeAnalysis', rejectScreenAnalysis: 'prototypeAnalysis', produceScreenCode:'prototypeAnalysis', zoneCode:'prototypeAnalysis', getScreenCodeSource:'prototypeAnalysis', checkScreenCodeSource:'prototypeAnalysis', saveScreenCodeSource:'prototypeAnalysis',
  listSimulations: 'simulation',
  getSimulation: 'simulation',
  createSimulation: 'simulation',
  updateSimulation: 'simulation',
  generateSimulationData: 'simulation',
  refreshSimulation: 'simulation',
  acknowledgeSimulationWarnings: 'simulation',
  deleteSimulation: 'simulation',
  createSimulationShare: 'simulation',
  revokeSimulationShare: 'simulation',

  listIdeas: 'ideas',
  createIdea: 'ideas',
  getIdea: 'ideas',
  updateIdea: 'ideas',
  ideaEstimate: 'ideas',
  fetchIdeaRepository: 'ideas',
  getIdeaRepository: 'ideas',
  startBrief: 'ideas',
  acceptBrief: 'ideas',
  startPrototype: 'ideas',
  getPrototypeHtml: 'ideas',
  getIdeaPrompt: 'ideas',
  cancelIdea: 'ideas',
  transferIdea: 'ideas',
  deleteIdea: 'ideas',

  listDossiers: 'dossiers',
  getDossier: 'dossiers',
  getDossierThread: 'dossiers',
  saveDossier: 'dossiers',
  deleteDossier: 'dossiers',
  getModuleSpec: 'dossiers',
  putModuleSpec: 'dossiers',
  listSpecs: 'specs',
  getSpec: 'specs',
  createSpec: 'specs',
  updateSpec: 'specs',
  listSpecVersions: 'specs',
  getSpecVersion: 'specs',
  diffSpec: 'specs',
  generateSpec: 'specs',
  syncSpec: 'specs',
  listTestPlans: 'testPlans',
  getTestPlan: 'testPlans',
  saveTestPlan: 'testPlans',
  listTestRuns: 'testRuns',
  getTestRun: 'testRuns',
  createTestRun: 'testRuns',
  putTestResult: 'testRuns',
  clearTestResult: 'testRuns',
  validateProposedResult: 'testRuns',
  putSceneNote: 'testRuns',
  closeTestRun: 'testRuns',
  listPrototypeImports: 'prototypeImports',
  getPrototypeImport: 'prototypeImports',
  createPrototypeImport: 'prototypeImports',
  addPrototypeSource: 'prototypeImports',
  addPrototypeImage: 'prototypeImports',
  getPrototypePrompt: 'prototypeImports',
  getPrototypePieceText: 'prototypeImports',
  getPrototypePieceImage: 'prototypeImports',
  addPrototypeSnapshot: 'prototypeImports',
  deletePrototypeSnapshot: 'prototypeImports',
  putPrototypeInventory: 'prototypeImports',
  getPrototypeInventory: 'prototypeImports',
  getModuleComponents: 'moduleComponents',
  refreshModuleComponents: 'moduleComponents',
  getCapabilityCatalog: 'capabilityCatalog',
  getPrototypeCoverage: 'capabilityCatalog',
  getSpecCoverage: 'capabilityCatalog',
  getGapReport: 'capabilityCatalog',
  getNavigationProposal: 'navigationFidelity',
  patchPrototypeImport: 'prototypeImports',
  deletePrototypeImport: 'prototypeImports',
  prototypeRemovalImpact: 'prototypeImports',
  restorePrototypeImport: 'prototypeImports',
  hidePrototypeImport: 'prototypeImports',
  acknowledgePrototypeWarnings: 'prototypeImports',
  getAiRoute: 'prototypeAnalysis',
  getPrototypeAnalysisEstimate: 'prototypeAnalysis',
  startPrototypeAnalysis: 'prototypeAnalysis',
  listPrototypeAnalyses: 'prototypeAnalysis',
  getPrototypeAnalysis: 'prototypeAnalysis',
  applyPrototypeAnalysis: 'prototypeAnalysis',
  rejectPrototypeAnalysis: 'prototypeAnalysis',
  listModuleSpecVersions: 'moduleSpec',
  getModuleSpecVersion: 'moduleSpec',
  diffModuleSpecVersions: 'moduleSpec',
  proposeModuleSpecVersion: 'moduleSpec',
  decideModuleSpecVersion: 'moduleSpec',
  previewModuleSpecRepair: 'moduleSpec',
  previewRelations: 'moduleSpec',
  applyRelations: 'moduleSpec',
  repairModuleSpecVersion: 'moduleSpec',
  getDeliveryCheck: 'deliveryCheck',
  estimateModuleSpecTranslation: 'translation',
  translateModuleSpecVersion: 'translation',
  createModuleSpecTranslationJob: 'translationJobs',
  listModuleSpecTranslationJobs: 'translationJobs',
  getModuleSpecTranslationJob: 'translationJobs',
  cancelModuleSpecTranslationJob: 'translationJobs',
  resumeModuleSpecTranslationJob: 'translationJobs',
  listReferenceCaptures: 'referenceCaptures',
  uploadReferenceCapture: 'referenceCaptures',
  getReferenceCaptureBytes: 'referenceCaptures',
  deleteReferenceCapture: 'referenceCaptures',
  listModuleAssets: 'presentation',
  uploadModuleAsset: 'presentation',
  copyModuleAssetFromPiece: 'presentation',
  getModuleAssetBytes: 'presentation',
  deleteModuleAsset: 'presentation',
  getPrototypePresentation: 'presentation',
  validateModuleSpec: 'moduleSpec',
  validateModuleSpecText: 'moduleSpec',
  getModuleSpecDraft: 'designer',
  putModuleSpecDraft: 'designer',
  createDraftFromVersion: 'designer',
  proposeModuleSpecDraft: 'designer',
  deleteModuleSpecDraft: 'designer',
  exportModuleToDraft: 'designer',
  previewModuleExport: 'designer',
  getDesignerCatalog: 'moduleSpec',
  listPreviewDatasets: 'preview',
  getPreviewDataset: 'preview',
  createPreviewDataset: 'preview',
  updatePreviewDataset: 'preview',
  generatePreviewRows: 'preview',
  refreshPreviewSnapshot: 'preview',
  acknowledgePreviewWarnings: 'preview',
  deletePreviewDataset: 'preview',
  createPreviewShare: 'preview',
  revokePreviewShare: 'preview',
  suggestedMigrationDate: 'generator',
  listGenerationRuns: 'generator',
  getGenerationRun: 'generator',
  createGenerationRun: 'generator',
  deleteGenerationRun: 'generator',
  readGeneratorFile: 'generator',
  downloadGeneratorBundle: 'generator',
  runGeneratorStripe: 'generatorStripe',
  depositGeneratorRun: 'generatorRepo',
  getPipelineEnvironments: 'pipeline',
  updatePipelineEnvironment: 'pipeline',
  listPipelineTenants: 'pipeline',
  listPipelineRuns: 'pipeline',
  getPipelineRun: 'pipeline',
  readPipelineEnvironment: 'pipeline',
  deletePipelineRun: 'pipeline',
  planPipelinePromotion: 'pipelineData',
  executePipelineRun: 'pipelineData',
  rollbackPipelineRun: 'pipelineData',
  approvePipelineRun: 'pipelineGates',
  getPipelineGates: 'pipelineGates',
  getPipelineJourney: 'pipelineGates',
  openPipelineEnvironment: 'pipelineGates',
  listFeedback: 'feedback',
  getFeedback: 'feedback',
  createFeedback: 'feedback',
  updateFeedback: 'feedback',
  classifyFeedback: 'feedback',
  transmitFeedback: 'feedback',
  getFeedbackTransmission: 'feedback',
  resolveFeedback: 'feedback',
  rejectFeedback: 'feedback',
  reopenFeedback: 'feedback',
};

export class SimulationSpecErrorsError extends Error {
  readonly issues: GeneratorIssue[];
  constructor(issues: GeneratorIssue[]) { super('simulation.spec_errors'); this.name='SimulationSpecErrorsError'; this.issues=issues; }
}
